/**
 * Workload-profile catalog — single source of truth for the 7 workload types
 * the platform actually distinguishes scoring / provider / prompt behavior on.
 *
 * Replaces the older `projectType` field (5 values where only 2 had wired
 * behavior). Every value here maps to:
 *   - data-provider gating (recommendProviders in dataProviders/index.ts)
 *   - scoring useCase ('ai_dc' / 'standard' / 'smr_hybrid' via deriveUseCase)
 *   - FeasibilityPreferences.customerCategory (intentToPreferences.ts)
 *   - per-prompt guidance strings for test-fit / interconnection prompts
 *
 * `mixed` is the explicit "AI infers from Brief + power" path — different from
 * "undecided" because the AI is told to make a call rather than treat it as
 * an unknown.
 *
 * Migration: existing sites' `projectIntent.projectType` is preserved on the
 * record; reads go through `effectiveWorkloadProfile(intent)` which falls
 * back to the legacy field. New writes set `workloadProfile` and leave
 * `projectType` alone.
 */

export type WorkloadProfile =
  | 'ai-training'
  | 'ai-inference'
  | 'hpc'
  | 'enterprise'
  | 'colocation'
  | 'edge'
  | 'mixed';

/**
 * Canonical read result. `unknown` is not a selectable workload profile; it is
 * the epistemic state produced when the user explicitly records the field as
 * unknown. Keeping it outside `WorkloadProfile` prevents it entering profile
 * pickers, persisted enums, or workload-specific engineering catalogues.
 */
export type EffectiveWorkloadProfile = WorkloadProfile | 'unknown';

/**
 * Catalog entry — every behavior surface in the platform reads from these
 * fields. New surfaces should add a field here rather than branching on
 * `profile.id` at the call site (the original projectType tech-debt).
 */
export interface WorkloadProfileEntry {
  id: WorkloadProfile;
  label: string;
  /** One-liner shown under the option title in pickers. */
  hint: string;
  /** Hover-tooltip body — fuller explanation surfaced via shadcn Tooltip. */
  tooltip: string;
  /** Short context phrase used inside the AI PROJECT INTENT prompt block. */
  summary: string;
  /** Longer instruction injected into test-fit + interconnection prompts. */
  aiPromptHint: string;
  /** Drives `deriveUseCase`. nuclear-primary still wins regardless. */
  scoringUseCase: 'ai_dc' | 'standard';
  /**
   * Provider IDs (matching PROVIDER_CATALOG entries in dataProviders/index.ts)
   * that should be recommended ON TOP of the always-on defaults whenever this
   * profile is selected. Empty array = no extras. Keep IDs aligned with the
   * catalog or recommendProviders silently drops them.
   */
  extraDataProviders: ReadonlyArray<string>;
  /** Density anchor for test-fit prompts (kW per rack). */
  powerDensityKwRack: { min: number; max: number };
  /**
   * Bullet list shown in the "What this choice changes" tooltip / read-mode
   * card. Each line is a one-sentence concrete consequence (provider added,
   * scoring weight set, prompt language shifted) — keeps the picker UI
   * truthful about what the user is actually opting into.
   */
  impactSummary: ReadonlyArray<string>;
}

export type ExplicitUnknownWorkloadProfileEntry = Omit<WorkloadProfileEntry, 'id'> & {
  id: 'unknown';
};

export type EffectiveWorkloadProfileEntry =
  | WorkloadProfileEntry
  | ExplicitUnknownWorkloadProfileEntry;

export const WORKLOAD_PROFILES: ReadonlyArray<WorkloadProfileEntry> = [
  {
    id: 'ai-training',
    label: 'AI Training Datacenter',
    hint: 'High density (>50 kW/rack), water-heavy cooling, sustained 24h',
    tooltip: 'Dense GPU clusters running multi-week training jobs. Liquid cooling typical (CDU loops). Power ramps stay flat near nameplate; site needs strong utility commitment + redundant cooling water.',
    summary: 'dense GPU clusters running sustained multi-week training jobs',
    aiPromptHint: 'Plan for liquid cooling at >50 kW/rack with sustained near-nameplate power. East-west bandwidth between adjacent halls dominates the network design; <20 ms RTT to AWS/Azure/GCP for training-data ingress is non-negotiable.',
    scoringUseCase: 'ai_dc',
    extraDataProviders: ['peeringdb-ix'],
    powerDensityKwRack: { min: 50, max: 100 },
    impactSummary: [
      'Density anchor: 50–100 kW/rack with liquid cooling (CDU loops) assumed',
      'Adds PeeringDB IX proximity to the data-provider sweep',
      'Scoring uses the ai_dc weights (grid 35%, cooling 30%)',
      'Test-fit allocates ≥30% of net buildable for cooling-water infrastructure',
      'Interconnection prompt targets <20 ms RTT to AWS / Azure / GCP regions',
    ],
  },
  {
    id: 'ai-inference',
    label: 'AI Inference Datacenter',
    hint: 'Lower density, latency-sensitive, near population centres',
    tooltip: 'Spiky request-response workloads. Lower per-rack power than training but tight latency budgets to end users — pick sites near major metros and IX hubs. Air or hybrid cooling common.',
    summary: 'latency-sensitive request/response inference near user populations',
    aiPromptHint: 'Optimise for proximity to user populations and IX density: target <5 ms RTT to the dominant metro, ≥3 carrier choices on diverse entry. Hybrid cooling is fine; density is moderate, fibre matters more than land.',
    scoringUseCase: 'ai_dc',
    extraDataProviders: ['peeringdb-ix'],
    powerDensityKwRack: { min: 15, max: 25 },
    impactSummary: [
      'Density anchor: 15–25 kW/rack with hybrid cooling',
      'Adds PeeringDB IX proximity (latency to users is critical)',
      'Scoring uses the ai_dc weights',
      'Test-fit prioritises fibre-vault placement over raw acreage',
      'Interconnection prompt targets <5 ms RTT to the dominant metro, ≥3 carriers',
    ],
  },
  {
    id: 'hpc',
    label: 'HPC Compute Campus',
    hint: 'Training-like density, scientific workloads, batch-tolerant',
    tooltip: 'National-lab / research-grade clusters. Density similar to AI training but workloads are batch-scheduled, so power can ride on time-of-use rates. Often co-located with research network access (Internet2, ESnet).',
    summary: 'batch-scheduled scientific workloads with research-network peering',
    aiPromptHint: 'Density is training-like but workloads are batch-tolerant — sizing can ride time-of-use electricity rates. Prioritise research-network peering (Internet2, ESnet) over commercial transit; bulk dataset ingress is the dominant traffic pattern.',
    scoringUseCase: 'ai_dc',
    extraDataProviders: [],
    powerDensityKwRack: { min: 30, max: 50 },
    impactSummary: [
      'Density anchor: 30–50 kW/rack with batch-tolerant power profile',
      'No extra providers — research peering is out-of-band of the catalog today',
      'Scoring uses the ai_dc weights',
      'Test-fit plans for tape-library annexes + scientific receiving docks',
      'Interconnection prompt prefers Internet2 / ESnet over commercial transit',
    ],
  },
  {
    id: 'enterprise',
    label: 'Enterprise Compute',
    hint: 'Commodity density (10-15 kW/rack), longer asset lifecycle',
    tooltip: 'Corporate campus or single-tenant private cloud. Modest density, conventional cooling, longer refresh cycles. Site selection driven by labour cost / proximity to HQ rather than raw kW.',
    summary: 'single-tenant corporate compute with phased buildout',
    aiPromptHint: 'Standard transit (≥2 carriers, dual-homed BGP) is enough — no special peering needed. Phased buildout dominates: lay out for two halls of ~30k SF each with utility corridor between. Site selection follows labour cost / HQ proximity, not raw kW.',
    scoringUseCase: 'standard',
    extraDataProviders: [],
    powerDensityKwRack: { min: 10, max: 15 },
    impactSummary: [
      'Density anchor: 10–15 kW/rack commodity load',
      'No extra providers beyond defaults',
      'Scoring uses the standard balanced weights',
      'Test-fit emphasises phased buildout (two ~30k SF halls + utility corridor)',
      'Interconnection assumes generic dual-homed transit',
    ],
  },
  {
    id: 'colocation',
    label: 'Multi-tenant Colocation',
    hint: 'Carrier-neutral, modular, MMR rooms, mixed tenant load profiles',
    tooltip: 'Wholesale or retail colo serving multiple tenants. Diverse carrier ingress is critical (≥4 carriers, dual entry). Tenant power profiles vary widely; design must support 5-30 kW/rack out of the gate.',
    summary: 'multi-tenant carrier-neutral facility with mixed tenant load',
    aiPromptHint: 'Carrier-neutral MMR rooms with ≥4 distinct providers on dual-entry diverse paths are non-negotiable. Tenant SLAs typically demand Tier III network (N+1) — quantify failure-mode coverage. Design must accept 5-30 kW/rack mixed loads on day 1.',
    scoringUseCase: 'ai_dc',
    extraDataProviders: ['peeringdb-ix'],
    powerDensityKwRack: { min: 5, max: 30 },
    impactSummary: [
      'Density anchor: 5–30 kW/rack mixed-tenant range',
      'Adds PeeringDB IX proximity (carrier diversity is critical)',
      'Scoring uses the ai_dc weights',
      'Test-fit requires MMR rooms on every phase + ≥4 carriers on diverse entry',
      'Interconnection prompt enforces Tier III (N+1) failure-mode quantification',
    ],
  },
  {
    id: 'edge',
    label: 'Edge / Micro Datacenter',
    hint: '<5 MW, low-latency to end users, often unmanned',
    tooltip: 'Sub-5 MW deployments inside metros, at cell-tower bases, or co-located with CDN POPs. Often modular / containerised. Latency to user population dominates over land cost.',
    summary: 'sub-5 MW micro deployment co-located with users / CDN POPs',
    aiPromptHint: 'Latency to end users dominates over land cost: <5 ms RTT to the served population, ≥3 mobile-carrier towers within 10 km. Often unmanned + containerised — drive-up cabinet access matters more than expandability.',
    scoringUseCase: 'standard',
    extraDataProviders: ['peeringdb-ix'],
    powerDensityKwRack: { min: 5, max: 10 },
    impactSummary: [
      'Density anchor: 5–10 kW/rack, sub-5 MW total deployment',
      'Adds PeeringDB IX proximity for last-mile latency analysis',
      'Scoring uses the standard weights (size-override applies if >200 MW)',
      'Test-fit prioritises drive-up cabinet access + security perimeter',
      'Interconnection targets <5 ms RTT to user population, mobile-carrier proximity',
    ],
  },
  {
    id: 'mixed',
    label: 'Mixed / Not yet decided',
    hint: 'AI infers closest profile from Brief + power requirement',
    tooltip: 'No firm workload yet. The pipeline reads the site Brief + powerRequirement and picks the closest profile for scoring + prompts; flagged as low-confidence in the conclusion.',
    summary: 'workload not yet decided — assume enterprise-grade as the safe default',
    aiPromptHint: 'No firm workload yet. Default to enterprise-grade layout (10-15 kW/rack, two phased halls) but call out in the conclusion that the workload assumption is the dominant uncertainty and the user should firm up workloadProfile before final design.',
    scoringUseCase: 'standard',
    extraDataProviders: [],
    powerDensityKwRack: { min: 10, max: 30 },
    impactSummary: [
      'Default density anchor: 10–30 kW/rack (enterprise-grade)',
      'No extra providers beyond defaults',
      'Scoring uses standard weights — over-200mw still escalates to ai_dc',
      'Test-fit defaults to phased enterprise layout',
      'Conclusion flags the workload assumption as the dominant uncertainty',
    ],
  },
];

const _profileById = new Map<WorkloadProfile, WorkloadProfileEntry>(
  WORKLOAD_PROFILES.map(p => [p.id, p]),
);

const EXPLICIT_UNKNOWN_WORKLOAD_PROFILE: EffectiveWorkloadProfileEntry = {
  id: 'unknown',
  label: 'Unknown / not yet decided',
  hint: 'The user explicitly kept the workload open',
  tooltip: 'No workload-specific density, cooling, network, or operating assumptions are authorized until the operator confirms the workload.',
  summary: 'workload explicitly recorded as unknown; confirmation remains open',
  aiPromptHint: 'The user explicitly recorded the workload as unknown. Do not infer, default, or relabel it as mixed, enterprise, AI, HPC, or colocation. State the gap and recommend operator confirmation before workload-specific design conclusions.',
  // Unknown is not a scoring classification. Generic callers that only need a
  // safe non-AI branch retain the standard baseline; size and nuclear signals
  // remain independently authoritative in `deriveUseCase`.
  scoringUseCase: 'standard',
  extraDataProviders: [],
  // Never consume this range for engineering. `testFitGuidance` explicitly
  // returns before numerical formatting when id === 'unknown'.
  powerDensityKwRack: { min: 0, max: 0 },
  impactSummary: [
    'No workload-specific rack-density or cooling assumption is applied',
    'Reports state the unknown and recommend operator confirmation',
  ],
};

export function getWorkloadProfile(id: WorkloadProfile): WorkloadProfileEntry;
export function getWorkloadProfile(id: 'unknown'): ExplicitUnknownWorkloadProfileEntry;
export function getWorkloadProfile(id: EffectiveWorkloadProfile): EffectiveWorkloadProfileEntry;
export function getWorkloadProfile(id: EffectiveWorkloadProfile): EffectiveWorkloadProfileEntry {
  if (id === 'unknown') return EXPLICIT_UNKNOWN_WORKLOAD_PROFILE;
  const found = _profileById.get(id);
  if (!found) throw new Error(`Unknown workloadProfile: ${id}`);
  return found;
}

/* ── Legacy migration ─────────────────────────────────────────────────── */

/**
 * One-shot migration: map a legacy `projectType` value to the closest new
 * `workloadProfile`. The mapping is intentionally pessimistic — the legacy
 * `ai-datacenter` lumped training and inference together, so we map it to
 * `ai-training` (the more demanding of the two; downstream code handles
 * over-allocation more gracefully than under-allocation). Callers should
 * NOT write back to `projectType` after migrating; let the legacy field
 * decay naturally.
 */
export function migrateProjectTypeToWorkload(projectType: string | undefined): WorkloadProfile {
  switch (projectType) {
    case 'ai-datacenter': return 'ai-training';
    case 'hpc':           return 'hpc';
    case 'edge':          return 'edge';
    case 'colocation':    return 'colocation';
    case 'enterprise':    return 'enterprise';
    case 'undecided':
    case '':
    case undefined:
      return 'mixed';
    default:
      return 'mixed';
  }
}

/**
 * Read the effective workloadProfile from a ProjectIntent-shaped object,
 * preferring the new field when set and falling back to the legacy
 * `projectType` via migrateProjectTypeToWorkload. A durable
 * `fieldSource.workloadProfile = 'user-unknown'` marker wins over both fields
 * and resolves to `unknown`; truly absent/unmarked legacy records retain their
 * historical `mixed` fallback. Never throws.
 */
export function effectiveWorkloadProfile(
  intent: {
    workloadProfile?: WorkloadProfile | string;
    projectType?: string;
    fieldSource?: { workloadProfile?: string };
  } | undefined,
): EffectiveWorkloadProfile {
  if (!intent) return 'mixed';
  if (intent.fieldSource?.workloadProfile === 'user-unknown') return 'unknown';
  const explicit = intent.workloadProfile;
  if (explicit && _profileById.has(explicit as WorkloadProfile)) {
    return explicit as WorkloadProfile;
  }
  return migrateProjectTypeToWorkload(intent.projectType);
}

/* ── Scoring useCase derivation ──────────────────────────────────────── */

/**
 * Bucket the intent into the scoring engine's useCase. Reads
 * `profile.scoringUseCase` from the table — adding a new workload profile
 * with `scoringUseCase: 'ai_dc'` automatically opts it into the dense /
 * grid-heavy weight set without touching this function.
 *
 *   nuclearInterest='primary'        → 'smr_hybrid' (regulatory weight wins)
 *   profile.scoringUseCase='ai_dc'   → 'ai_dc'
 *   over-200mw on a 'standard' profile → 'ai_dc'    (size override)
 *   else                             → 'standard'
 */
/** Power-requirement buckets at/above 200 MW (incl. the legacy 'over-200mw'
 *  value) — treated as a hyperscale AI-datacenter signal. */
const HIGH_POWER_BUCKETS: ReadonlySet<string> = new Set([
  '200-500mw',
  '500-1000mw',
  '1000-1500mw',
  'over-1500mw',
  'over-200mw', // legacy
]);

export function deriveUseCase(intent: {
  workloadProfile?: WorkloadProfile | string;
  projectType?: string;
  fieldSource?: { workloadProfile?: string };
  nuclearInterest?: string;
  powerRequirement?: string;
}): 'ai_dc' | 'standard' | 'smr_hybrid' {
  if (intent.nuclearInterest === 'primary') return 'smr_hybrid';
  const profile = getWorkloadProfile(effectiveWorkloadProfile(intent));
  if (profile.scoringUseCase !== 'standard') return profile.scoringUseCase;
  // Any ≥200 MW bucket (the new ladder splits the old 'over-200mw' band into
  // finer buckets; 'over-200mw' is kept as a legacy value) reads as a
  // hyperscale AI datacenter.
  if (HIGH_POWER_BUCKETS.has(intent.powerRequirement ?? '')) return 'ai_dc';
  return 'standard';
}

/* ── Per-prompt guidance ─────────────────────────────────────────────── */

/**
 * Test-fit prompt fragment — composes the profile's `aiPromptHint` with the
 * concrete kW/rack density anchor so the model has both the qualitative
 * direction and a numeric range to plan against.
 */
export function testFitGuidance(intent: Parameters<typeof effectiveWorkloadProfile>[0]): string {
  const p = getWorkloadProfile(effectiveWorkloadProfile(intent));
  if (p.id === 'unknown') return p.aiPromptHint;
  const { min, max } = p.powerDensityKwRack;
  return `${p.aiPromptHint} Use the power-density range ${min}-${max} kW/rack as the layout planning anchor.`;
}

/**
 * Interconnection prompt fragment — `aiPromptHint` already covers RTT,
 * carrier diversity, and peering preference per profile, so this is just a
 * thin pass-through. Kept as a named helper so call sites in
 * geminiService.ts stay symmetric with testFitGuidance.
 */
export function interconnectionGuidance(intent: Parameters<typeof effectiveWorkloadProfile>[0]): string {
  return getWorkloadProfile(effectiveWorkloadProfile(intent)).aiPromptHint;
}
